/**
 * Phase 3 — OptimisationListPage
 *
 * Lists every design optimisation study in the tenant. Filters by
 * status / owner / free-text. Row click → detail page.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Crosshair } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import optimisation from "@/api/optimisation";
import { useAuth } from "@/auth/AuthContext";

const STATUS_FILTERS = [
    { value: "", label: "All statuses" },
    { value: "queued", label: "Queued" },
    { value: "running", label: "Running" },
    { value: "succeeded", label: "Succeeded" },
    { value: "partial", label: "Partial" },
    { value: "failed", label: "Failed" },
    { value: "cancelled", label: "Cancelled" },
];

export default function OptimisationListPage() {
    const { user } = useAuth();
    const [statusFilter, setStatusFilter] = useState("");
    const [ownerFilter, setOwnerFilter] = useState("all");
    const [q, setQ] = useState("");
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const fetchStudies = async () => {
        setLoading(true);
        setErr(null);
        try {
            const params = { limit: 200 };
            if (statusFilter) params.status = statusFilter;
            if (ownerFilter === "mine") params.owner = user?.username;
            if (q) params.q = q;
            const list = await optimisation.listStudies(params);
            setStudies(Array.isArray(list) ? list : []);
        } catch (e) {
            setErr(formatApiError(e, "Failed to load studies"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStudies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, ownerFilter, q]);

    const filtered = useMemo(() => studies, [studies]);

    return (
        <div className="space-y-4" data-testid="optimisation-list-page">
            <PageHeader
                icon={Crosshair}
                title="Design Optimisation"
                description="Search the design space for a configuration that hits a Pk target at minimum design cost."
                actions={
                    <Button asChild data-testid="optimisation-list-new">
                        <Link to="/run/optimisation/new">
                            <Plus className="w-3.5 h-3.5 mr-1" /> New study
                        </Link>
                    </Button>
                }
            />

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                    <Search className="w-3 h-3 text-ux-text-3 absolute left-2 top-1/2 -translate-y-1/2" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search study name…"
                        className="pl-7 bg-ux-bg-1 max-w-[260px] h-8 text-[12px]"
                        data-testid="optimisation-list-search"
                    />
                </div>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger
                        className="w-[160px] h-8 bg-ux-bg-1 text-[12px]"
                        data-testid="optimisation-list-status-filter"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STATUS_FILTERS.map((s) => (
                            <SelectItem key={s.value || "all"} value={s.value || "all"}>
                                {s.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                    <SelectTrigger
                        className="w-[140px] h-8 bg-ux-bg-1 text-[12px]"
                        data-testid="optimisation-list-owner-filter"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Anyone</SelectItem>
                        <SelectItem value="mine">My studies</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {err && (
                <div
                    className="rounded-md border border-ux-danger/40 bg-ux-danger-soft/30 p-3 text-[12px] text-ux-danger"
                    data-testid="optimisation-list-error"
                >
                    {err}
                </div>
            )}

            <div className="rounded-md border border-ux-border-1 bg-ux-bg-1 overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-ux-bg-2">
                            <TableHead className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                Name
                            </TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                Status
                            </TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                Profile
                            </TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-ux-text-3 text-right">
                                Best Pk
                            </TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                Owner
                            </TableHead>
                            <TableHead className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                Created
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={6} className="py-8 text-center text-ux-text-3 text-[12px]">
                                    Loading…
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && filtered.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="py-12 text-center text-ux-text-3 text-[12px]"
                                    data-testid="optimisation-list-empty"
                                >
                                    No studies yet. Click <span className="text-ux-text-2">"New study"</span>{" "}
                                    above to compose one.
                                </TableCell>
                            </TableRow>
                        )}
                        {filtered.map((s) => {
                            const bestPk = s?.progress?.current_best_pk;
                            return (
                                <TableRow
                                    key={s.id}
                                    className="hover:bg-ux-bg-2/40 cursor-pointer"
                                    data-testid={`optimisation-list-row-${s.id}`}
                                >
                                    <TableCell>
                                        <Link
                                            to={`/run/optimisation/${s.id}`}
                                            className="text-[13px] text-ux-text-1 hover:underline"
                                        >
                                            {s.name}
                                        </Link>
                                        <div className="text-[10px] font-mono text-ux-text-3">
                                            {s.id}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <StatusPill status={s.status} />
                                    </TableCell>
                                    <TableCell className="text-[12px] text-ux-text-2">
                                        {s.profile || "—"}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums text-[12px]">
                                        {typeof bestPk === "number" ? bestPk.toFixed(3) : "—"}
                                    </TableCell>
                                    <TableCell className="text-[12px] text-ux-text-2">
                                        {s.owner_username || "—"}
                                    </TableCell>
                                    <TableCell className="text-[11px] font-mono text-ux-text-3">
                                        {s.created_at ? s.created_at.slice(0, 16) : "—"}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
